import React from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Headphones, Mail, MessageCircle } from 'lucide-react';
import CustomerSupportChat from '@/components/CustomerSupportChat';
import { PageBreadcrumb } from '@/components/seo/PageBreadcrumb';
import { getPublicSiteUrl } from '@/lib/seo';
import { renderSocialMetaTags } from '@/components/seo/SocialMetaTags';
import { getLocaleFromPathname, buildPathForLocale } from '@/lib/intl-seo';

const SupportPage = () => {
  const { lang } = useParams();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const langCode =
    lang || getLocaleFromPathname(location.pathname) || (i18n.language || 'en').substring(0, 2);
  const site = getPublicSiteUrl();
  const homePath = buildPathForLocale(langCode, []);
  const contactPath = buildPathForLocale(langCode, ['contact']);
  const pageTitle = `${t('support.title', 'Müşteri Desteği')} | Turkish E-Market`;
  const canonical = site
    ? `${site.replace(/\/$/, '')}${buildPathForLocale(langCode, ['support'])}`
    : '';

  return (
    <div className="bg-background min-h-[60vh]" data-testid="support-page">
      <Helmet>
        <title>{pageTitle}</title>
        <meta
          name="description"
          content={t(
            'support.metaDescription',
            'Turkish E-Market müşteri desteği — sipariş, kargo, iade ve ürünler hakkında 7/24 AI destekli yardım.',
          )}
        />
        {canonical && <link rel="canonical" href={canonical} />}
        {renderSocialMetaTags({
          title: pageTitle,
          description: t(
            'support.metaDescription',
            'Turkish E-Market müşteri desteği — sipariş, kargo, iade ve ürünler hakkında 7/24 AI destekli yardım.',
          ),
          url: canonical,
          image: site ? `${site.replace(/\/$/, '')}/logo.png` : '',
          locale: langCode,
        })}
      </Helmet>

      <div className="container mx-auto px-4 py-8 md:py-10 max-w-4xl">
        <PageBreadcrumb
          items={[
            { label: t('nav.home', 'Ana Sayfa'), href: homePath },
            { label: t('support.title', 'Müşteri Desteği') },
          ]}
        />

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 text-primary mb-4">
            <Headphones className="w-7 h-7" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
            {t('support.title', 'Müşteri Desteği')}
          </h1>
          <p className="text-muted-foreground text-sm md:text-base max-w-xl mx-auto">
            {t(
              'support.subtitle',
              'Sipariş, kargo, iade veya ürünler hakkında sorularınız için aşağıdaki sohbetten bize yazın.',
            )}
          </p>
        </div>

        <CustomerSupportChat embedded />

        <div className="mt-8 grid sm:grid-cols-2 gap-4 text-sm">
          <div className="rounded-lg border p-4 flex items-start gap-3">
            <MessageCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">{t('support.liveChat', 'Canlı destek')}</p>
              <p className="text-muted-foreground text-xs mt-1">
                {t('support.liveChatHint', 'Yukarıdaki sohbet kutusu 10 dilde yapay zeka destekli yanıt verir.')}
              </p>
            </div>
          </div>
          <div className="rounded-lg border p-4 flex items-start gap-3">
            <Mail className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">{t('support.emailTitle', 'E-posta')}</p>
              <p className="text-muted-foreground text-xs mt-1">
                <a href="mailto:support@turkishemarket.com" className="text-primary hover:underline">
                  support@turkishemarket.com
                </a>
              </p>
              <Link to={contactPath} className="text-xs text-primary hover:underline mt-1 inline-block">
                {t('support.contactPage', 'İletişim sayfası')} →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportPage;
